import { z } from 'zod';
// User Roles - Based on new schema (Organization -> Tenant -> Users)
// Combined enum with display names for better maintainability
export var UserRole;
(function (UserRole) {
    // Platform level (SystemUser)
    UserRole["SUPER_ADMIN"] = "SUPER_ADMIN";
    // Organization level
    UserRole["ORG_HEAD"] = "ORG_HEAD";
    // Tenant level - Admin
    UserRole["TENANT_HEAD"] = "TENANT_HEAD";
    UserRole["PRINCIPAL"] = "PRINCIPAL";
    // Staff roles
    UserRole["TEACHER"] = "TEACHER";
    UserRole["HR_MANAGER"] = "HR_MANAGER";
    UserRole["ACCOUNTANT"] = "ACCOUNTANT";
    // Sales roles
    UserRole["SALES_MANAGER"] = "SALES_MANAGER";
    UserRole["SALES_REP"] = "SALES_REP";
    // Other roles
    UserRole["PR_MANAGER"] = "PR_MANAGER";
    UserRole["TRANSPORT_MANAGER"] = "TRANSPORT_MANAGER";
    UserRole["DRIVER"] = "DRIVER";
    // End users
    UserRole["PARENT"] = "PARENT";
    UserRole["STUDENT"] = "STUDENT";
})(UserRole || (UserRole = {}));
// Role metadata - Display names and descriptions
export const USER_ROLES = {
    [UserRole.SUPER_ADMIN]: { value: UserRole.SUPER_ADMIN, label: 'Super Admin', description: 'Full system access' },
    [UserRole.ORG_HEAD]: { value: UserRole.ORG_HEAD, label: 'Organization Head', description: 'Manages all tenants in organization' },
    [UserRole.TENANT_HEAD]: { value: UserRole.TENANT_HEAD, label: 'Tenant Admin', description: 'Administrator for specific tenant' },
    [UserRole.PRINCIPAL]: { value: UserRole.PRINCIPAL, label: 'Principal', description: 'School principal' },
    [UserRole.TEACHER]: { value: UserRole.TEACHER, label: 'Teacher', description: 'Teaching staff' },
    [UserRole.HR_MANAGER]: { value: UserRole.HR_MANAGER, label: 'HR Manager', description: 'Human resources management' },
    [UserRole.SALES_MANAGER]: { value: UserRole.SALES_MANAGER, label: 'Sales Manager', description: 'Manages sales team' },
    [UserRole.SALES_REP]: { value: UserRole.SALES_REP, label: 'Sales Representative', description: 'Handles leads and sales' },
    [UserRole.PR_MANAGER]: { value: UserRole.PR_MANAGER, label: 'PR Manager', description: 'Parent relationship management' },
    [UserRole.TRANSPORT_MANAGER]: { value: UserRole.TRANSPORT_MANAGER, label: 'Transport Manager', description: 'Manages transportation' },
    [UserRole.ACCOUNTANT]: { value: UserRole.ACCOUNTANT, label: 'Accountant', description: 'Financial management' },
    [UserRole.DRIVER]: { value: UserRole.DRIVER, label: 'Driver', description: 'Vehicle driver' },
    [UserRole.PARENT]: { value: UserRole.PARENT, label: 'Parent', description: 'Student parent/guardian' },
    [UserRole.STUDENT]: { value: UserRole.STUDENT, label: 'Student', description: 'Enrolled student' },
};
// Utility function to get role display name
export const getRoleDisplayName = (role) => {
    return USER_ROLES[role]?.label || role;
};
// Utility function to get role description
export const getRoleDescription = (role) => {
    return USER_ROLES[role]?.description || '';
};
export var UserStatus;
(function (UserStatus) {
    UserStatus["ACTIVE"] = "ACTIVE";
    UserStatus["INACTIVE"] = "INACTIVE";
    UserStatus["SUSPENDED"] = "SUSPENDED";
    UserStatus["PENDING"] = "PENDING";
})(UserStatus || (UserStatus = {}));
// Zod Schemas - Updated for new database schema
export const UserSchema = z.object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(), // Changed from organizationId to tenantId
    username: z.string().min(3).max(50),
    email: z.string().email(),
    emailVerified: z.boolean().default(false),
    // Role handling - primary role (most users have single role)
    role: z.nativeEnum(UserRole),
    roles: z.array(z.nativeEnum(UserRole)).optional(), // For dual-role users
    // Status
    isActive: z.boolean().default(true),
    isDeleted: z.boolean().default(false),
    // Auth
    lastLoginAt: z.date().optional(),
    fcmTokens: z.array(z.string()).default([]),
    // Timestamps
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().optional(),
});
// SystemUser schema (for SUPER_ADMIN and platform-level admins)
export const SystemUserSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid().optional(), // Optional for multi-org admins
    name: z.string().min(1).max(255),
    username: z.string().min(3).max(100),
    email: z.string().email(),
    isSuperAdmin: z.boolean().default(false),
    isActive: z.boolean().default(true),
    lastLoginAt: z.date().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// UserProfile schema (separated from User)
export const UserProfileSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    fullName: z.string().min(1).max(200),
    dateOfBirth: z.date().optional(),
    gender: z.enum(['MALE', 'FEMALE', 'OTHER']).optional(),
    avatar: z.string().url().optional(),
    bio: z.string().optional(),
    addressId: z.string().uuid().optional(),
    contactId: z.string().uuid().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
export const CreateUserSchema = UserSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    lastLoginAt: true,
    deletedAt: true,
    isDeleted: true,
});
export const UpdateUserSchema = CreateUserSchema.partial();
// Login schemas - Updated for new hierarchy
export const UserLoginSchema = z.object({
    identifier: z.string().min(1), // email or username
    password: z.string().min(6),
    organizationId: z.string().uuid(), // Required for regular users
});
export const SystemUserLoginSchema = z.object({
    identifier: z.string().min(1), // email or username  
    password: z.string().min(6),
    // No organizationId for super admin
});
export const ChangePasswordSchema = z.object({
    currentPassword: z.string().min(6),
    newPassword: z.string().min(6),
    confirmPassword: z.string().min(6),
}).refine(data => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});
// User Factory Pattern
export class UserFactory {
    static createUser(role, data) {
        const baseUser = {
            id: crypto.randomUUID(),
            role,
            status: UserStatus.ACTIVE,
            fcmTokens: [],
            createdAt: new Date(),
            updatedAt: new Date(),
            ...data,
        };
        return UserSchema.parse(baseUser);
    }
    static createTeacher(data) {
        return this.createUser(UserRole.TEACHER, data);
    }
    static createParent(data) {
        return this.createUser(UserRole.PARENT, data);
    }
    static createStudent(data) {
        return this.createUser(UserRole.STUDENT, data);
    }
}
// User Permissions
export class UserPermissions {
    static roleHierarchy = {
        [UserRole.SUPER_ADMIN]: 100,
        [UserRole.ORG_HEAD]: 95,
        [UserRole.TENANT_HEAD]: 90,
        [UserRole.PRINCIPAL]: 80,
        [UserRole.HR_MANAGER]: 70,
        [UserRole.SALES_MANAGER]: 65,
        [UserRole.PR_MANAGER]: 63,
        [UserRole.TRANSPORT_MANAGER]: 60,
        [UserRole.ACCOUNTANT]: 55,
        [UserRole.TEACHER]: 50,
        [UserRole.SALES_REP]: 40,
        [UserRole.DRIVER]: 30,
        [UserRole.PARENT]: 20,
        [UserRole.STUDENT]: 10,
    };
    static canAccess(userRole, requiredRole) {
        return this.roleHierarchy[userRole] >= this.roleHierarchy[requiredRole];
    }
    static canManageUser(managerRole, targetRole) {
        return this.roleHierarchy[managerRole] > this.roleHierarchy[targetRole];
    }
    static getAccessibleRoles(userRole) {
        const userLevel = this.roleHierarchy[userRole];
        return Object.entries(this.roleHierarchy)
            .filter(([_, level]) => level < userLevel)
            .map(([role, _]) => role);
    }
    static canViewAttendance(userRole, targetUserId, currentUserId) {
        if (userRole === UserRole.SUPER_ADMIN || userRole === UserRole.ORG_HEAD || userRole === UserRole.TENANT_HEAD || userRole === UserRole.PRINCIPAL) {
            return true;
        }
        if (userRole === UserRole.TEACHER) {
            return true; // Teachers can view their students' attendance
        }
        if (userRole === UserRole.PARENT) {
            return true; // Parents can view their children's attendance (checked at service level)
        }
        return targetUserId === currentUserId; // Users can view their own attendance
    }
    static canApproveLeave(userRole) {
        return [
            UserRole.SUPER_ADMIN,
            UserRole.ORG_HEAD,
            UserRole.TENANT_HEAD,
            UserRole.PRINCIPAL,
            UserRole.HR_MANAGER,
            UserRole.TEACHER,
        ].includes(userRole);
    }
    static canManagePayroll(userRole) {
        return [
            UserRole.SUPER_ADMIN,
            UserRole.ORG_HEAD,
            UserRole.TENANT_HEAD,
            UserRole.HR_MANAGER,
            UserRole.ACCOUNTANT,
        ].includes(userRole);
    }
}
//# sourceMappingURL=User.js.map